import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getSeoData } from "@/seo/config";

function setMeta(attr: "name" | "property", key: string, content?: string) {
  const el = document.head.querySelector(
    `meta[${attr}="${key}"]`,
  ) as HTMLMetaElement | null;

  if (!content) {
    if (el) el.remove();
    return;
  }

  if (el) {
    el.setAttribute("content", content);
    return;
  }

  const created = document.createElement("meta");
  created.setAttribute(attr, key);
  created.setAttribute("content", content);
  document.head.appendChild(created);
}

function setCanonical(href: string) {
  let link = document.head.querySelector(
    'link[rel="canonical"]',
  ) as HTMLLinkElement | null;

  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }

  link.setAttribute("href", href);
}

// Route-aware head manager: updates document.title and the description,
// keywords, canonical, Open Graph and Twitter Card tags on every route change
// so metadata never leaks from the previous route.
export function useSeo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = getSeoData(pathname);

    document.title = seo.title;

    setMeta("name", "description", seo.description);
    setMeta("name", "keywords", seo.keywords);
    setCanonical(seo.canonical);

    setMeta("property", "og:type", seo.ogType);
    setMeta("property", "og:title", seo.ogTitle);
    setMeta("property", "og:description", seo.ogDescription);
    setMeta("property", "og:locale", seo.ogLocale);
    setMeta("property", "og:url", seo.ogUrl);
    setMeta("property", "og:site_name", seo.ogSiteName);
    setMeta("property", "og:image", seo.ogImage);

    setMeta("name", "twitter:card", seo.twitterCard);
    setMeta("name", "twitter:title", seo.twitterTitle);
    setMeta("name", "twitter:description", seo.twitterDescription);
    setMeta("name", "twitter:image", seo.twitterImage);
  }, [pathname]);
}